import dayjs from 'dayjs';
import { camelCase, lowerCase, omit, uniqBy } from 'lodash-es';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { TASK_CONST } from '@/constant';
import { TASK_FRAME_WORK_STATE_TRANSLATION_KEY_MAPPER } from '@/constant/taskConstant';
import { useTypedSearchParams, useVerfiyModal } from '@/hook';
import type { VStatusBadgeProps, VTableColumnFilter } from '@/package';
import { VFlex, VStatusBadge, VTable, Verified } from '@/package';
import { VIconButton } from '@/package/kiska/src/component/UI/VIconButton';
import { useAuthStore } from '@/store';
import type { FrameData, FrameSearchParams, Task } from '@/type';
import { getFrameState } from '@/util/frame';

import { ReviewerHandoff } from '../Task';

const FrameTable = ({
  frameList,
  task,
}: {
  frameList?: FrameData[];
  task?: Task;
}) => {
  const navigate = useNavigate();
  const user = useAuthStore(state => state.user);
  const rowRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { getAll, append, remove, flush, get } =
    useTypedSearchParams<FrameSearchParams>();

  const onRowClick = (frame: FrameData) => {
    navigate(`/labeling/${frame.projectID}/${frame.taskID}/${frame.frameSeq}`);
  };

  const renderedFrameList = useMemo(() => {
    return (
      frameList?.map(frame => {
        const { frameSeq } = frame;
        return {
          id: `${frameSeq}`,
          status: getFrameState(frame, task),
          assignee:
            frame.worker?.name || frame.worker?.email.split('@').at(0) || '-',
          reviewer:
            frame.reviewer?.name ||
            frame.reviewer?.email.split('@').at(0) ||
            '-',
          startDate: frame.workStartTime
            ? dayjs(frame.workStartTime).format('YYYY-MM-DD')
            : '-',
          completionDate: frame.reviewEndTime
            ? dayjs(frame.reviewEndTime).format('YYYY-MM-DD')
            : '-',
          verify: null,

          task,
          frame,
        };
      }) ?? []
    );
  }, [frameList, task]);

  const taskUserList = task?.userTask?.map(ut => ut.user) ?? [];

  const statusColumnFilterItemList = Object.values(
    omit(TASK_CONST.TASK_FRAME_WORKSTATE, 'VIEW_ONLY'),
  ).map(status => ({
    value: status,
    label: t(
      TASK_FRAME_WORK_STATE_TRANSLATION_KEY_MAPPER[
        lowerCase(
          status,
        ) as keyof typeof TASK_FRAME_WORK_STATE_TRANSLATION_KEY_MAPPER
      ],
    ),
    isSelected: getAll('frameWorkState')?.includes(status) ?? false,
  }));

  const assigneeColumnFilterItemList = taskUserList.map(user => ({
    value: user?.id,
    label: user?.name || user?.email.split('@').at(0) || '-',
    isSelected:
      (user?.id && getAll('frameWorkerId')?.includes(user.id)) ?? false,
  }));

  const reviewerColumnFilterItemList = uniqBy(
    taskUserList.concat(user),
    'id',
  ).map(user => ({
    value: user?.id,
    label: user?.name || user?.email.split('@').at(0) || '-',
    isSelected:
      (user?.id && getAll('frameReviewerId')?.includes(user.id)) ?? false,
  }));

  const columnFilter = [
    {
      id: 'status',
      itemList: statusColumnFilterItemList,
    },
    {
      id: 'assignee',
      itemList: assigneeColumnFilterItemList,
    },
    {
      id: 'reviewer',
      itemList: reviewerColumnFilterItemList,
    },
  ];

  return (
    <VTable
      ref={rowRef}
      data={renderedFrameList}
      className="flex-1 overflow-y-auto"
      onRowClick={e => onRowClick(e._target.row.frame)}
      setRowSelected={() => true}
      columnList={[
        'id',
        'status',
        'assignee',
        'reviewer',
        'startDate',
        'completionDate',
        'verify',
      ]}
      columnStyleMap={{
        id: { className: 'w-[64px]' },
        status: { className: 'w-[104px]' },
        assignee: { className: 'w-full' },
        reviewer: { className: 'w-full' },
        startDate: { className: 'w-[124px]' },
        completionDate: { className: 'w-[124px]' },
        verify: { className: 'w-[68px]' },
      }}
      columnRenderer={{
        id: () => <div className="text-body-lg">No.</div>,
      }}
      cellRenderer={{
        id: value => value,
        status: value =>
          value ? (
            <VStatusBadge
              size="sm"
              variant={
                camelCase(value.toLowerCase()) as VStatusBadgeProps['variant']
              }
            >
              {value}
            </VStatusBadge>
          ) : null,
        assignee: value => value,
        reviewer: (value, _, row) => {
          return (
            <VFlex justify="between">
              {value}
              <div
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                {row.task &&
                  row.frame.workState ===
                    TASK_CONST.TASK_FRAME_WORKSTATE.REVIEWING &&
                  row.frame.reviewerID === user.id && (
                    <ReviewerHandoff.EachButton
                      frameSeq={[row.frame.frameSeq]}
                      task={row.task}
                    />
                  )}
              </div>
            </VFlex>
          );
        },
        startDate: value => value,
        completionDate: value => value,
        verify: (_1, _2, row) => <VerlfiyButton frame={row.frame} />,
      }}
      columnSorter={[
        {
          id: 'id',
          desc: get('frameOrderBy') === '-frameSeq' ? true : false,
        },
      ]}
      onColumnSorterChange={e => {
        remove('frameOrderBy');
        const idSorter = e.find(sorter => sorter.id === 'id');
        idSorter?.desc
          ? append('frameOrderBy', '-frameSeq')
          : append('frameOrderBy', '+frameSeq');
        flush();
      }}
      columnFilter={
        columnFilter as VTableColumnFilter<(typeof renderedFrameList)[number]>[]
      }
      onColumnFilterChange={e => {
        const statusColumnFilter = e.find(filter => filter.id === 'status');
        const assigneeColumnFilter = e.find(filter => filter.id === 'assignee');
        const reviewerColumnFilter = e.find(filter => filter.id === 'reviewer');

        remove('frameWorkState');
        remove('frameWorkerId');
        remove('frameReviewerId');

        statusColumnFilter?.itemList
          .filter(item => item.isSelected)
          .forEach(item => {
            append('frameWorkState', item.value);
          });

        assigneeColumnFilter?.itemList
          .filter(item => item.isSelected)
          .forEach(item => {
            append('frameWorkerId', item.value);
          });

        reviewerColumnFilter?.itemList
          .filter(item => item.isSelected)
          .forEach(item => {
            append('frameReviewerId', item.value);
          });

        flush();
      }}
    />
  );
};

const VerlfiyButton = ({ frame }: { frame: FrameData }) => {
  const { openVerifyModal } = useVerfiyModal({
    frameSeq: frame.frameSeq,
    taskId: frame.taskID,
  });
  const { user } = useAuthStore();
  const isAcceptable =
    (frame.workState === TASK_CONST.TASK_FRAME_WORKSTATE.REVIEWING ||
      frame.workState === TASK_CONST.TASK_FRAME_WORKSTATE.REVIEW) &&
    frame.reviewer?.id === user.id;

  return (
    <VFlex className="w-full" justify="center" items="center">
      <VIconButton
        Icon={Verified}
        onClick={e => {
          e.stopPropagation();
          openVerifyModal();
        }}
        disabled={!isAcceptable}
      />
    </VFlex>
  );
};

export default FrameTable;
